// SPDX-License-Identifier: AGPL-3.0-only
import { useState } from 'react';
import { Alert, Badge, Button, Code, Group, Modal, Stack, Text } from '@mantine/core';

import type { Device } from '../../api/devices';

/**
 * SSH host identity — the trust decision, in front of a person.
 *
 * # WHY THIS IS A PANEL AND NOT A TOAST
 *
 * It is the decision that lets Meshvara send a device's management password to a
 * machine. Everything else on the device page describes what a switch is doing;
 * this decides whether Meshvara will talk to it at all.
 *
 * # FOUR STATES, AND THEY LOOK DIFFERENT
 *
 *	untrusted  nothing has been seen. No password will be sent.
 *	pending    a key was presented and nobody has confirmed it.
 *	trusted    an operator confirmed the exact key.
 *	changed    a key was confirmed and the device presented a DIFFERENT one.
 *
 * `changed` is the loud one. The network is working; the machine answering is
 * not provably the one that was trusted, and that can mean a replaced switch, a
 * factory reset, or somebody on the wire.
 *
 * # THERE IS NO "CONTINUE ANYWAY"
 *
 * Deliberately, and it is the most important omission in this file. Replacing a
 * trusted identity is a separate button behind a separate dialog with its own
 * audit action, so it cannot happen as a way of getting past an error message.
 *
 * # WHAT "TRUSTED" MEANS HERE, AND WHAT IT DOES NOT
 *
 * It means A PERSON EXPLICITLY TRUSTED THE FIRST IDENTITY THIS DEVICE PRESENTED.
 * It does not mean Meshvara proved the machine is the switch they meant — no
 * software can prove that from the network side, which is exactly why a human is
 * asked.
 *
 * The wording follows that. An operator often has no independently-known
 * fingerprint, and a dialog implying they must go and find one is a dialog that
 * either blocks legitimate work or teaches people to click past a warning they
 * cannot satisfy. Independent verification is offered as something to do IF THEY
 * CAN, never as a precondition.
 *
 * # THE OPERATOR DOES NOT TYPE A FINGERPRINT
 *
 * They look at it and press a button. The binding is server-side and exact —
 * see ConfirmTrust.
 */

export function SshTrust({
  device,
  canModify,
  probing,
  onProbe,
  onTrust,
  onReplace,
}: {
  device: Device;
  canModify: boolean;
  probing: boolean;
  onProbe: () => void;
  onTrust: (pendingHostKey: string) => Promise<void>;
  onReplace: (pendingHostKey: string) => Promise<void>;
}) {
  const [dialog, setDialog] = useState<'none' | 'trust' | 'replace'>('none');

  const state = device.ssh_trust_state;

  return (
    <>
      {state === 'trusted' ? (
        <Group gap="xs">
          <Badge color="teal" variant="light">
            SSH identity trusted
          </Badge>
          <Text size="xs" c="dimmed" ff="monospace">
            {device.ssh_trusted_fingerprint}
          </Text>
        </Group>
      ) : null}

      {state === 'untrusted' ? (
        <Alert color="blue" variant="light" title="This device has not been contacted">
          <Stack gap="sm" align="flex-start">
            <Text size="sm">
              Meshvara has not seen this device's SSH identity yet. Nothing will be sent to it —
              including its password — until you have seen the key it presents and trusted it.
            </Text>
            {canModify ? (
              <Button size="xs" loading={probing} onClick={onProbe}>
                Check SSH identity
              </Button>
            ) : null}
          </Stack>
        </Alert>
      ) : null}

      {state === 'pending' ? (
        <Alert color="blue" variant="light" title="New SSH device">
          <Stack gap="sm" align="flex-start">
            <Text size="sm">
              This is the first SSH identity Meshvara has seen at this device. Trusting it lets
              Meshvara authenticate here — and nothing is sent until you do.
            </Text>
            <Fingerprint
              address={`${device.management_address}:${device.management_port}`}
              expected={device.driver_id}
              algorithm={device.ssh_pending_algorithm}
              fingerprint={device.ssh_pending_fingerprint}
            />
            {canModify ? (
              <Group>
                <Button size="xs" onClick={() => setDialog('trust')}>
                  Trust &amp; continue
                </Button>
                <Button size="xs" variant="default" loading={probing} onClick={onProbe}>
                  Check again
                </Button>
              </Group>
            ) : null}
          </Stack>
        </Alert>
      ) : null}

      {state === 'changed' ? (
        // THE LOUDEST THING ON THE PAGE. A filled red alert, not a light one:
        // this is the single state where "somebody is on the wire" is a live
        // possibility, and it must not read like an ordinary error.
        <Alert color="red" variant="filled" title="SSH device identity changed">
          <Stack gap="sm" align="flex-start">
            <Text size="sm">
              This device is presenting a different SSH host key from the one Meshvara trusts.{' '}
              <b>No password has been sent.</b>
            </Text>
            <Stack gap={2}>
              <Text size="xs">
                Previously trusted: <Code>{device.ssh_trusted_fingerprint}</Code>
              </Text>
              <Text size="xs">
                Presented now: <Code>{device.ssh_pending_fingerprint}</Code>
              </Text>
            </Stack>
            <Text size="sm">
              This can happen after a device replacement, a factory reset, SSH host keys being
              regenerated, or an address being reassigned. It can also mean the connection is being
              intercepted.
            </Text>
            {canModify ? (
              <Button size="xs" variant="white" color="red" onClick={() => setDialog('replace')}>
                Review new key
              </Button>
            ) : null}
          </Stack>
        </Alert>
      ) : null}

      <ConfirmTrust
        opened={dialog !== 'none'}
        replacing={dialog === 'replace'}
        device={device}
        onCancel={() => setDialog('none')}
        onConfirm={async (pendingHostKey) => {
          const act = dialog === 'replace' ? onReplace : onTrust;
          await act(pendingHostKey);
          setDialog('none');
        }}
      />
    </>
  );
}

function Fingerprint({
  address,
  expected,
  algorithm,
  fingerprint,
}: {
  address: string;
  expected: string;
  algorithm?: string;
  fingerprint?: string;
}) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed">
        Address
      </Text>
      <Text size="sm" ff="monospace">
        {address}
      </Text>
      <Text size="xs" c="dimmed" mt={4}>
        Expected device
      </Text>
      <Text size="sm">{expected}</Text>
      <Text size="xs" c="dimmed" mt={4}>
        SSH key type
      </Text>
      <Text size="sm">{algorithm ?? '—'}</Text>
      <Text size="xs" c="dimmed" mt={4}>
        Fingerprint
      </Text>
      <Code>{fingerprint ?? '—'}</Code>
    </Stack>
  );
}

/**
 * The confirmation dialog.
 *
 * # NOBODY TYPES A FINGERPRINT, AND NOBODY IS BLOCKED ON HAVING ONE
 *
 * The operator looks at the fingerprint and presses a button — the workflow every
 * administrator already knows from PuTTY and OpenSSH. Comparing it against the
 * device's own console is worth doing WHERE THAT IS POSSIBLE, and is offered as
 * such; it is not a precondition, because most of the time there is nothing to
 * compare against and a prompt nobody can satisfy is a prompt people learn to
 * click through.
 *
 * The security binding is SERVER-SIDE and exact: the browser echoes back the
 * precise key it was shown, and the server accepts only if the device is still
 * presenting that key. So an approval applies to key A even if the device starts
 * presenting key B a second later, without the property depending on a human
 * retyping a digest correctly.
 *
 * Retyping would have been weaker AND worse to use, which is a rare combination
 * and a good reason to have removed it.
 */
function ConfirmTrust({
  opened,
  replacing,
  device,
  onCancel,
  onConfirm,
}: {
  opened: boolean;
  replacing: boolean;
  device: Device;
  onCancel: () => void;
  onConfirm: (pendingHostKey: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pendingKey = device.ssh_pending_host_key ?? '';

  return (
    <Modal
      opened={opened}
      onClose={busy ? () => undefined : onCancel}
      centered
      title={replacing ? 'Replace trusted SSH key' : 'Trust this SSH device?'}
    >
      <Stack>
        {replacing ? (
          <Alert color="red" variant="light">
            You are replacing the SSH identity Meshvara trusts for this device. Do this only if you
            know why the key changed.
          </Alert>
        ) : null}

        <Text size="sm">
          {replacing
            ? 'Meshvara will trust this key from now on, and will refuse if the device ever presents a different one.'
            : 'This is the first SSH identity Meshvara has seen at this device. Do you trust this device?'}
        </Text>

        <Fingerprint
          address={`${device.management_address}:${device.management_port}`}
          expected={device.driver_id}
          algorithm={device.ssh_pending_algorithm}
          fingerprint={device.ssh_pending_fingerprint}
        />

        {/* OFFERED, NOT REQUIRED. Most operators have no independently-known
            fingerprint, and a prerequisite nobody can meet is one they learn to
            click past — which would weaken every prompt after it. */}
        <Text size="xs" c="dimmed">
          If you have another way to verify this fingerprint, you may do so before continuing.
        </Text>

        {error ? (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        ) : null}

        <Group justify="flex-end">
          <Button variant="default" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            color={replacing ? 'red' : undefined}
            loading={busy}
            disabled={pendingKey === ''}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                // THE EXACT KEY, ECHOED BACK. Not the fingerprint, and not
                // "whatever is pending now": the server refuses if the device
                // has started presenting something else.
                await onConfirm(pendingKey);
              } catch (err) {
                setError(
                  err instanceof Error
                    ? err.message
                    : 'That key was not accepted. Check the device identity again.',
                );
              } finally {
                setBusy(false);
              }
            }}
          >
            {replacing ? 'Replace trusted key' : 'Trust & continue'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
